import React, { useState } from 'react';
import {
    Alert,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    InputLabel,
    Stack,
    TextField,
    Typography,
    CircularProgress
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import OrgPropertiesService from 'services/org-properties.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';

const ChangeEmailModal = ({
    onClose,
    currentEmail,
    orgId,
    propertyId,
    userId,
    onSuccess
}) => {
    const [submitting, setSubmitting] = useState(false);

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .trim()
            .lowercase()
            .email('Please enter a valid email address')
            .required('New email address is required')
    });

    return (
        <>
            <DialogTitle>Change Email Address</DialogTitle>
            <Divider />

            <Formik
                initialValues={{ email: '' }}
                validationSchema={validationSchema}
                onSubmit={async values => {
                    setSubmitting(true);

                    try {
                        const newEmail = values.email.trim().toLowerCase();

                        await OrgPropertiesService.changeEmailAddress(
                            orgId,
                            propertyId,
                            userId,
                            { email: newEmail }
                        );

                        SnackbarNotify.success(
                            `Email address has been updated to ${newEmail}`
                        );

                        onSuccess?.(newEmail);
                        onClose();
                    } catch (error) {
                        const message =
                            error?.response?.data?.message ||
                            error?.message ||
                            'Something went wrong while changing the email address. Please try again.';

                        SnackbarNotify.error(message);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    touched,
                    errors,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5 }}>
                            <Stack spacing={2}>
                                <Typography variant="body1">
                                    Current email address: <b>{currentEmail || '—'}</b>
                                </Typography>

                                <Alert severity="warning" variant="outlined">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Important
                                    </Typography>
                                    <Typography variant="body2">
                                        This updates the user&apos;s email address across
                                        the entire system. After confirmation, the user
                                        will need to sign in using the new email address.
                                    </Typography>
                                </Alert>

                                <Stack spacing={0.5}>
                                    <InputLabel>New Email Address</InputLabel>
                                    <TextField
                                        name="email"
                                        placeholder="email@example.com"
                                        fullWidth
                                        autoComplete="email"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        error={touched.email && !!errors.email}
                                        helperText={
                                            touched.email && errors.email
                                                ? errors.email
                                                : ''
                                        }
                                        disabled={submitting}
                                    />
                                </Stack>
                            </Stack>
                        </DialogContent>

                        <Divider />

                        <DialogActions sx={{ p: 2.5 }}>
                            <Button color="error" onClick={onClose} disabled={submitting}>
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <CircularProgress size={18} />
                                        <span>Confirm</span>
                                    </Stack>
                                ) : (
                                    'Confirm'
                                )}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default ChangeEmailModal;
